import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authKeys } from "./auth";
import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";

export interface MfaPolicy {
  require_mfa_for_all_members: boolean;
  mandatory_roles: ["admin", "approver"];
  reauthentication_required: boolean;
}

export const mfaPolicyKey = ["workspace-mfa-policy"] as const;

async function policyFrom(response: Response, message: string): Promise<MfaPolicy> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusalFrom(response, body, message);
  if (
    !body ||
    typeof body !== "object" ||
    !("require_mfa_for_all_members" in body) ||
    typeof body.require_mfa_for_all_members !== "boolean" ||
    !("reauthentication_required" in body) ||
    typeof body.reauthentication_required !== "boolean" ||
    !("mandatory_roles" in body) ||
    !Array.isArray(body.mandatory_roles) ||
    body.mandatory_roles.length !== 2 ||
    body.mandatory_roles[0] !== "admin" ||
    body.mandatory_roles[1] !== "approver"
  ) {
    throw new TypeError("The MFA policy response could not be read.");
  }
  return {
    require_mfa_for_all_members: body.require_mfa_for_all_members,
    mandatory_roles: ["admin", "approver"],
    reauthentication_required: body.reauthentication_required,
  };
}

export function useMfaPolicy(enabled: boolean) {
  return useQuery({
    queryKey: mfaPolicyKey,
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      policyFrom(
        await apiFetch("/api/v1/org/security/mfa"),
        "This workspace's MFA policy could not be read.",
      ),
  });
}

export function useUpdateMfaPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (requireForEveryone: boolean) =>
      policyFrom(
        await apiFetch("/api/v1/org/security/mfa", {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ require_mfa_for_all_members: requireForEveryone }),
        }),
        "The MFA policy could not be saved.",
      ),
    onSuccess: async (policy) => {
      queryClient.setQueryData(mfaPolicyKey, policy);
      if (!policy.reauthentication_required) {
        await queryClient.invalidateQueries({ queryKey: ["administration"] });
      }
    },
  });
}

export function useMfaPolicySignIn() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.cancelQueries();
    queryClient.setQueryData(authKeys.me(), null);
    queryClient.removeQueries({
      predicate: (query) => JSON.stringify(query.queryKey) !== JSON.stringify(authKeys.me()),
    });
  };
}
